/** REEL&SHORTS responsive layout engine — mirrors backend/reels_shorts_layout_engine.py */

export const REELS_FORMAT_CANVASES = [
  {
    id: '9:16',
    label: 'Reel / Shorts',
    shortLabel: 'Reel',
    width: 1080,
    height: 1920,
    useCases: ['Instagram Reels', 'YouTube Shorts', 'TikTok'],
  },
  {
    id: '1:1',
    label: 'Square',
    shortLabel: 'Square',
    width: 1080,
    height: 1080,
    useCases: ['Instagram Feed', 'LinkedIn', 'Facebook'],
  },
  {
    id: '4:5',
    label: 'Portrait feed',
    shortLabel: 'Portrait',
    width: 1080,
    height: 1350,
    useCases: ['Instagram Feed'],
  },
  {
    id: '16:9',
    label: 'YouTube',
    shortLabel: 'YouTube',
    width: 1920,
    height: 1080,
    useCases: ['YouTube', 'Presentations'],
  },
];

const FORMAT_IDS = new Set(REELS_FORMAT_CANVASES.map((row) => row.id));

const LAYOUT_SEEDS = {
  '9:16': {
    type: 'stack',
    face_tracking: true,
    priority: ['face', 'hook', 'captions', 'visual', 'cta'],
    columns: [{ width: 1, content: 'speaker' }],
  },
  '1:1': {
    type: 'split',
    face_tracking: true,
    priority: ['hook', 'video', 'subtitle'],
    columns: [
      { width: 0.5, content: 'speaker' },
      { width: 0.5, content: 'broll' },
    ],
  },
  '4:5': {
    type: 'stack',
    face_tracking: true,
    priority: ['face', 'hook', 'captions', 'cta'],
    columns: [{ width: 1, content: 'speaker' }],
  },
  '16:9': {
    type: 'split',
    face_tracking: true,
    priority: ['speaker', 'broll', 'captions'],
    columns: [
      { width: 0.58, content: 'speaker' },
      { width: 0.42, content: 'broll' },
    ],
  },
};

export function normalizeFormatId(value, fallback = '9:16') {
  const raw = String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  if (FORMAT_IDS.has(raw)) return raw;
  const aliases = {
    reel: '9:16',
    reels: '9:16',
    shorts: '9:16',
    vertical: '9:16',
    square: '1:1',
    portrait: '4:5',
    feed: '4:5',
    youtube: '16:9',
    landscape: '16:9',
  };
  return aliases[raw] || (FORMAT_IDS.has(fallback) ? fallback : '9:16');
}

export function formatMeta(id) {
  const key = normalizeFormatId(id);
  return REELS_FORMAT_CANVASES.find((row) => row.id === key) || REELS_FORMAT_CANVASES[0];
}

export function defaultLayoutForFormat(aspectRatio = '9:16') {
  const key = normalizeFormatId(aspectRatio);
  const seed = LAYOUT_SEEDS[key] || LAYOUT_SEEDS['9:16'];
  return {
    type: seed.type,
    face_tracking: Boolean(seed.face_tracking),
    priority: [...(seed.priority || [])],
    columns: (seed.columns || []).map((col) => ({ ...col })),
  };
}

export function emptyFormatsBlock(aspectRatio = '9:16') {
  const active = normalizeFormatId(aspectRatio);
  const compositions = {};
  REELS_FORMAT_CANVASES.forEach((row) => {
    compositions[row.id] = {
      canvas: {
        width: row.width,
        height: row.height,
        aspect_ratio: row.id,
        label: row.label,
      },
      layout: defaultLayoutForFormat(row.id),
      safe_zones: true,
      reframe: null,
    };
  });
  return {
    active,
    compositions,
    export_targets: REELS_FORMAT_CANVASES.map((row) => row.id),
  };
}

export function sanitizeFormatsBlock(raw, aspectRatio = '9:16') {
  const base = emptyFormatsBlock(aspectRatio);
  const data = raw && typeof raw === 'object' ? raw : {};
  const active = normalizeFormatId(data.active || aspectRatio, base.active);
  const compositions = { ...base.compositions };
  Object.entries(data.compositions || {}).forEach(([key, value]) => {
    const id = normalizeFormatId(key, '');
    if (!FORMAT_IDS.has(id) || !value || typeof value !== 'object') return;
    const meta = formatMeta(id);
    compositions[id] = {
      canvas: {
        width: Number(value.canvas?.width) || meta.width,
        height: Number(value.canvas?.height) || meta.height,
        aspect_ratio: id,
        label: meta.label,
      },
      layout: {
        ...defaultLayoutForFormat(id),
        ...(value.layout && typeof value.layout === 'object' ? value.layout : {}),
      },
      safe_zones: value.safe_zones == null ? true : Boolean(value.safe_zones),
      reframe: value.reframe || null,
    };
  });
  let exportTargets = Array.isArray(data.export_targets)
    ? data.export_targets.map((t) => normalizeFormatId(t, '')).filter((t) => FORMAT_IDS.has(t))
    : [];
  exportTargets = [...new Set(exportTargets)];
  if (!exportTargets.length) exportTargets = base.export_targets;
  return { active, compositions, export_targets: exportTargets };
}

export function activeComposition(formats) {
  const block = sanitizeFormatsBlock(formats);
  return block.compositions[block.active] || block.compositions['9:16'];
}

export function previewAspectStyle(aspectRatio = '9:16', maxSide = 120) {
  const meta = formatMeta(aspectRatio);
  const ratio = meta.width / meta.height;
  if (ratio >= 1) {
    return { width: maxSide, height: Math.round(maxSide / ratio) };
  }
  return { width: Math.round(maxSide * ratio), height: maxSide };
}

export function layoutColumnsLabel(layout) {
  const cols = layout?.columns || [];
  if (!cols.length) return 'fullscreen';
  return cols.map((c) => `${c.content} ${Math.round((Number(c.width) || 0) * 100)}%`).join(' · ');
}
